// MatReactTable.jsx
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {
    MaterialReactTable,
    useMaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ShowHideColumnsButton,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner,
    MRT_TablePagination,
    MRT_TableContainer
} from "material-react-table/";
import {
    Box,
    useTheme,
    Typography,
    CircularProgress,
    alpha,
    Button,
    Checkbox,
    Badge
} from "@mui/material/";
import tableStyles, { defaultMessages } from "./styles";
import { strings } from "../../i18n";
import { useTableState } from "../../hooks";
import ActiveFilters from "./ActiveFilters";

// Define row status styles function that uses theme
const getRowStatusStyles = (theme) => ({
    // Bold row style when assetActionStatus is present
    boldRow: {
        fontWeight: "bold",
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.3)
        }
    },

    // Normal row style when assetActionStatus is not present
    normalRow: {
        fontWeight: "normal",
        backgroundColor: "transparent",
        "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.3)
        }
    },

    // Disabled row style
    disabledRow: {
        opacity: 0.6,
        backgroundColor: "#fafafa",
        cursor: "not-allowed",
        "&:hover": {
            backgroundColor: "#fafafa"
        }
    }
});

const MatReactTable = ({
    data,
    columns,
    loading,
    rowSelection,
    rowActions,
    onRowClick,
    onSelectionChange,
    toolBarComponents,
    noRowsMessage,
    loadingMessage,
    noRecordsMessage,
    // New props for enhanced functionality
    enableRowSelection,
    muiCheckboxProps,
    getRowProps,
    getRowId,
    initialState,
    layoutMode,
    enableColumnResizing,
    enablePagination,
    enableRowVirtualization,
    renderDetailPanel,
    muiExpandButtonProps,
    muiExpandAllButtonProps,
    // "page" (MRT default) or "all" — see selectAllMode below
    selectAllMode,
    // Opt-in persistence of filters/search/sort/column-layout across navigation
    persistKey
}) => {
    const theme = useTheme();
    const [error, setError] = useState(null);
    const [rowSelectionState, setRowSelectionState] = useState(
        initialState?.rowSelection ?? {}
    );
    const rowSelectionRef = useRef(initialState?.rowSelection ?? {});

    // Persisted, controlled view state (no-op unless persistKey is provided)
    const {
        persisted: statePersisted,
        slices: persistedSlices,
        handlers: persistedHandlers
    } = useTableState(persistKey, columns, {
        columnVisibility: initialState?.columnVisibility,
        columnPinning: {
            right: ["mrt-row-actions"],
            ...(initialState?.columnPinning || {})
        },
        sorting: initialState?.sorting
    });

    // Use custom messages or defaults
    const loadingMsg = loadingMessage || defaultMessages.loading;
    const noRecordsMsg =
        noRecordsMessage || noRecordsMessage || defaultMessages.noRecords;

    // Performance Optimization: Memoize row status styles
    const rowStatusStyles = useMemo(() => getRowStatusStyles(theme), [theme]);

    // Reset error when data loads successfully
    useEffect(() => {
        if (data && !loading) {
            setError(null);
        }
    }, [data, loading]);

    // Custom checkbox for selection with disabled state support
    const renderRowSelectCheckbox = useMemo(
        () =>
            ({ row, table }) => {
                const isSelectable = enableRowSelection
                    ? enableRowSelection(row)
                    : true;

                // Get custom checkbox props
                const customCheckboxProps = muiCheckboxProps
                    ? muiCheckboxProps({ row })
                    : {};

                // If row is not selectable, render disabled checkbox
                if (!isSelectable) {
                    return (
                        <Checkbox
                            disabled
                            checked={false}
                            sx={{
                                "&.Mui-disabled": {
                                    color: theme.palette.action.disabled
                                },
                                ...customCheckboxProps.sx
                            }}
                            {...customCheckboxProps}
                        />
                    );
                }

                return (
                    <Checkbox
                        checked={row.getIsSelected()}
                        onChange={row.getToggleSelectedHandler()}
                        aria-label="Select row"
                        sx={customCheckboxProps.sx}
                        {...customCheckboxProps}
                    />
                );
            },
        [enableRowSelection, muiCheckboxProps, theme]
    );

    // Handle selection changes — keeps internal state in sync so MRT renders correctly
    const handleRowSelectionChange = useCallback(
        (updater) => {
            const newSelection =
                typeof updater === "function"
                    ? updater(rowSelectionRef.current)
                    : updater;
            rowSelectionRef.current = newSelection;
            setRowSelectionState(newSelection);

            if (!onSelectionChange) return;
            const selectedRowData = Object.keys(newSelection)
                .filter((key) => newSelection[key])
                .map((key) =>
                    data?.find(
                        (row) => row.id === key || row.id?.toString() === key
                    )
                )
                .filter(Boolean);
            onSelectionChange(selectedRowData);
        },
        [onSelectionChange, data]
    );

    // Performance Optimization: Memoize table configuration
    const tableConfig = useMemo(() => {
        const config = {
            columns,
            data: data || [],
            defaultColumn: {
                sortingFn: "alphanumeric"
            },
            enableColumnFilterModes: true,
            enableClickToCopy: true,
            enableColumnOrdering: true,
            enableFacetedValues: true,
            enableGlobalFilter: true,
            enableRowSelectionOnClick: true,
            enableRowActions: !!rowActions,
            enableRowSelection: !!rowSelection,
            // MRT defaults selectAllMode to "page", so the header checkbox
            // only ticks the rows on screen. Callers whose bulk action must
            // cover the whole filtered list (Review approve: a 50-row page
            // silently approved 50 of a 300-asset audit) pass "all". Kept
            // opt-in because this table backs ~19 screens, and on e.g. Create
            // step 1 "all" would tick every branch on every page. Pagination
            // here is client-side (no manualPagination), so every filtered row
            // is loaded and "all" genuinely means all. Rows the caller marks
            // unselectable via enableRowSelection are still skipped by MRT.
            selectAllMode: selectAllMode ?? "page",
            ...(getRowId && { getRowId }),
            layoutMode: layoutMode ?? "grid",
            enableColumnResizing: enableColumnResizing ?? true,
            columnResizeMode: "onChange",
            enablePagination: enablePagination ?? true,
            enableRowVirtualization: enableRowVirtualization ?? false,

            // Enhanced row props with getRowProps support
            muiTableBodyRowProps: ({ row }) => {
                const { assetActionStatus } = row.original;
                let rowStyle = rowStatusStyles.normalRow;

                if (assetActionStatus) {
                    rowStyle = rowStatusStyles.boldRow;
                }

                const isSelectable = enableRowSelection
                    ? enableRowSelection(row)
                    : true;
                const isClickable = onRowClick && isSelectable;
                const isSelectableRow = rowSelection && isSelectable;

                if (!isSelectable) {
                    rowStyle = { ...rowStyle, ...rowStatusStyles.disabledRow };
                }

                const customRowProps = getRowProps ? getRowProps({ row }) : {};
                const customSx = customRowProps.sx || {};

                return {
                    onClick: (event) => {
                        if (isClickable) {
                            onRowClick(row.original);
                        } else if (isSelectableRow) {
                            row.getToggleSelectedHandler()(event);
                        }
                    },
                    onKeyDown: (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            if (isClickable) {
                                onRowClick(row.original);
                            } else if (isSelectableRow) {
                                row.getToggleSelectedHandler()(event);
                            }
                        }
                    },
                    tabIndex: isClickable || isSelectableRow ? 0 : -1,
                    role: isClickable || isSelectableRow ? "button" : "row",
                    "aria-label":
                        isClickable || isSelectableRow
                            ? `Select row ${row.index + 1}`
                            : undefined,
                    "aria-disabled": !isSelectable,
                    sx: {
                        ...tableStyles.tableBodyRow,
                        ...rowStyle,
                        ...customSx,
                        cursor:
                            isClickable || isSelectableRow
                                ? "pointer"
                                : !isSelectable
                                ? "not-allowed"
                                : "default",
                        "&:focus":
                            isClickable || isSelectableRow
                                ? {
                                      outline: `2px solid ${theme.palette.primary.main}`,
                                      outlineOffset: "-2px"
                                  }
                                : {}
                    },
                    ...customRowProps
                };
            },

            muiTablePaperProps: {
                sx: tableStyles.tablePaper
            },

            muiTableProps: {
                sx: tableStyles.table
            },

            muiTableHeadProps: {
                sx: tableStyles.tableHead
            },

            muiTableHeadCellProps: ({ column }) => ({
                sx: {
                    ...tableStyles.tableHeadCell,
                    fontFamily: theme.typography.fontFamily,
                    "& svg.MuiTableSortLabel-icon": {
                        color: `${theme.palette.primary.main} !important`
                    },
                    "& svg.MuiSvgIcon-root": {
                        color: theme.palette.primary.main
                    },
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    backgroundColor: theme.palette.background.paper,
                    "&[data-pinned='true']": {
                        backgroundColor: theme.palette.background.paper,
                        zIndex: 4
                    },
                    // Highlight headers of columns that currently have a filter
                    ...(column.getIsFiltered() && {
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderBottom: `2px solid ${theme.palette.primary.main}`
                    })
                }
            }),

            muiTableBodyCellProps: ({ cell }) => {
                const isPinned = cell.column.getIsPinned();
                return {
                    sx: {
                        ...tableStyles.tableBodyCell,
                        fontFamily: theme.typography.fontFamily,
                        "&:hover": {
                            backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1
                            )
                        },
                        ...(isPinned && {
                            backgroundColor: theme.palette.background.paper, // Use theme background
                            position: "sticky",
                            zIndex: 2,
                            ...(isPinned === "right" && {
                                "&:hover": {
                                    borderLeft: `1px solid ${theme.palette.divider}`
                                }
                            })
                        })
                    }
                };
            },

            muiSearchTextFieldProps: {
                placeholder: strings("search"),
                sx: tableStyles.searchTextField
            },

            muiPaginationProps: {
                rowsPerPageOptions: [50, 100, 250, 500],
                sx: tableStyles.pagination,
                showFirstButton: true,
                showLastButton: true
            },

            muiCheckboxProps: {
                sx: tableStyles.selectionCheckbox
            },

            muiIconButtonProps: {
                sx: tableStyles.toolbarButtons
            },

            muiTopToolbarProps: {
                sx: tableStyles.topToolbar
            },

            muiBottomToolbarProps: {
                sx: tableStyles.bottomToolbar
            },

            muiTableContainerProps: {
                sx: tableStyles.tableContainer
            },

            // Enhanced Loading States
            muiSkeletonProps: {
                animation: "wave",
                variant: "rectangular",
                height: 20,
                sx: {
                    ...tableStyles.skeleton,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                }
            },

            muiLinearProgressProps: {
                color: "primary"
            },

            muiCircularProgressProps: {
                color: "primary"
            },

            // Enhanced Empty State
            renderEmptyRowsFallback: () => (
                <Box sx={tableStyles.emptyContainer}>
                    <Typography sx={tableStyles.emptyText}>
                        {noRecordsMsg}
                    </Typography>
                </Box>
            ),

            initialState: {
                density: "compact",
                showGlobalFilter: true,
                showColumnFilters: false,
                columnPinning: {
                    right: ["mrt-row-actions"]
                },
                pagination: {
                    pageSize: 50,
                    pageIndex: 0
                },
                ...initialState
            },

            state: {
                isLoading: loading,
                rowSelection: rowSelectionState
            },

            paginationDisplayMode: "pages",
            positionToolbarAlertBanner: "bottom"
        };

        // Add row actions if provided
        if (rowActions) {
            config.renderRowActions = ({ row }) => rowActions(row);
        }

        // Add row selection features if enabled
        if (rowSelection) {
            config.renderRowSelectCheckbox = renderRowSelectCheckbox;
            config.onRowSelectionChange = handleRowSelectionChange;
        }

        // Add enableRowSelection if provided
        if (enableRowSelection) {
            config.enableRowSelection = enableRowSelection;
        }

        // Add detail panel if provided
        if (renderDetailPanel) {
            config.renderDetailPanel = renderDetailPanel;
        }

        config.displayColumnDefOptions = {
            "mrt-row-expand": {
                size: 40,
                minSize: 40,
                maxSize: 40,
                muiTableHeadCellProps: { sx: { padding: "0 4px" } },
                muiTableBodyCellProps: { sx: { padding: "0 4px" } }
            },
            "mrt-row-select": {
                size: 40,
                minSize: 40,
                maxSize: 40,
                muiTableHeadCellProps: { sx: { padding: "0 4px" } },
                muiTableBodyCellProps: { sx: { padding: "0 4px" } }
            },
            "mrt-row-actions": {
                header: "Actions",
                size: 160,
                minSize: 160,
                muiTableHeadCellProps: { align: "center" },
                muiTableBodyCellProps: { align: "center", sx: { overflow: "visible" } }
            }
        };

        // Add expand button props if provided (e.g. hide button on certain rows)
        if (muiExpandButtonProps) {
            config.muiExpandButtonProps = muiExpandButtonProps;
        }

        // Add expand-all button props if provided (e.g. hide the expand-all toggle)
        if (muiExpandAllButtonProps) {
            config.muiExpandAllButtonProps = muiExpandAllButtonProps;
        }

        // Persisted, controlled view state. Each controlled slice needs its
        // on*Change handler or MRT freezes it — both come from useTableState.
        if (statePersisted) {
            config.state = { ...config.state, ...persistedSlices };
            // Keep the actions column pinned right even if a persisted pinning
            // value predates it.
            if (rowActions) {
                const right = new Set([
                    ...(persistedSlices.columnPinning?.right || []),
                    "mrt-row-actions"
                ]);
                config.state.columnPinning = {
                    ...persistedSlices.columnPinning,
                    right: Array.from(right)
                };
            }
            Object.assign(config, persistedHandlers);
        }

        return config;
    }, [
        columns,
        data,
        loading,
        rowSelection,
        rowActions,
        onRowClick,
        onSelectionChange,
        enableRowSelection,
        getRowProps,
        muiCheckboxProps,
        rowStatusStyles,
        theme,
        noRecordsMsg,
        renderRowSelectCheckbox,
        handleRowSelectionChange,
        initialState,
        getRowId,
        rowSelectionState,
        renderDetailPanel,
        muiExpandButtonProps,
        muiExpandAllButtonProps,
        enableRowVirtualization,
        statePersisted,
        persistedSlices,
        persistedHandlers
    ]);

    const table = useMaterialReactTable(tableConfig);

    // Count of active column filters — surfaced as a badge so users can tell
    // filters are applied even when the filter panel is collapsed.
    const activeFilterCount = table.getState().columnFilters?.length || 0;

    const selectedRows = table.getSelectedRowModel().rows || [];
    const selectedRowData = selectedRows.map((r) => r.original);
    const selectedRowCount = selectedRows.length;

    // Enhanced Selection Features
    const totalRows = table.getFilteredRowModel().rows.length || 0;
    const allRowsSelected =
        selectedRowCount > 0 && selectedRowCount === totalRows;
    const someRowsSelected =
        selectedRowCount > 0 && selectedRowCount < totalRows;

    // Get pagination information
    const pagination = table.getState().pagination || {
        pageIndex: 0,
        pageSize: 10
    };
    const firstRow = pagination.pageIndex * pagination.pageSize + 1;
    const lastRow = Math.min(
        (pagination.pageIndex + 1) * pagination.pageSize,
        totalRows
    );

    // Error Boundary & Fallback
    if (error) {
        return (
            <Box sx={tableStyles.emptyContainer}>
                <Typography color="error" sx={tableStyles.emptyText}>
                    {strings("error_loading_data") || "Error loading data"}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => window.location.reload()}
                    sx={{ mt: 2 }}
                >
                    {strings("retry") || "Retry"}
                </Button>
            </Box>
        );
    }

    return (
        <>
            {/* Custom toolbar layout */}
            <Box sx={tableStyles.toolbarContainer}>
                <MRT_GlobalFilterTextField table={table} />
                <Box sx={tableStyles.buttonGroup}>
                    <Badge
                        badgeContent={activeFilterCount}
                        color="primary"
                        overlap="circular"
                    >
                        <MRT_ToggleFiltersButton
                            table={table}
                            sx={tableStyles.toolbarButtons}
                        />
                    </Badge>
                    <MRT_ShowHideColumnsButton
                        table={table}
                        sx={tableStyles.toolbarButtons}
                    />
                    {toolBarComponents && toolBarComponents(selectedRowData)}
                </Box>
            </Box>

            {/* Active-filter chips bar */}
            <ActiveFilters table={table} />

            {/* Table container with fixed height and sticky header */}
            <Box sx={{ ...tableStyles.tableContainer, position: "relative" }}>
                <MRT_TableContainer table={table} />
            </Box>

            <Box sx={tableStyles.bottomSection}>
                {/* Left side - Enhanced Selection count */}
                <Box sx={tableStyles.selectionCount}>
                    {rowSelection && selectedRowCount > 0 && (
                        <>
                            <Typography
                                variant="body2"
                                color="primary"
                                sx={{ fontWeight: "medium" }}
                            >
                                {selectedRowCount}{" "}
                                {selectedRowCount === 1 ? "row" : "rows"}{" "}
                                selected
                            </Typography>
                            {someRowsSelected && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mt: 0.5 }}
                                >
                                    ({selectedRowCount} of {totalRows})
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                {/* Center - Row count information */}
                <Box sx={tableStyles.rowInfo}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={tableStyles.rowInfoText}
                    >
                        {totalRows > 0 ? (
                            <>
                                {firstRow}–{lastRow} of {totalRows}{" "}
                                {totalRows === 1 ? "row" : "rows"}
                            </>
                        ) : (
                            ""
                        )}
                    </Typography>
                </Box>

                {/* Right side - Pagination controls */}
                <Box sx={tableStyles.paginationWrapper}>
                    <MRT_TablePagination table={table} />
                </Box>
            </Box>
        </>
    );
};

export default MatReactTable;
